/**
 * API Type Mappers
 * Maps between backend API types and frontend types
 *
 * Note: Uses type assertions where API schema types don't perfectly match
 * frontend types due to OpenAPI generator limitations with nullable fields
 */

import type * as API from './api-types';
import type * as Frontend from '../services/types';
import { translateApiError } from '@/utils/apiErrorTranslator';
import { formatCivilDate } from '@/utils/formatters';
import { addCivilDays } from '@/utils/wallClock';

// ============================================================================
// Patient Mappers
// ============================================================================

const coerceAddress = (raw: unknown): Frontend.Address => {
  if (raw && typeof raw === 'object' && 'street' in raw) {
    return raw as Frontend.Address;
  }
  // Fallback for legacy string addresses or missing data
  return {
    street: typeof raw === 'string' ? raw : '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  };
};

export const mapPatientFromApi = (apiPatient: any): Frontend.Patient => {
  return {
    id: apiPatient.id,
    // Portal access is exactly `user_id IS NOT NULL` server-side (the same rule
    // behind the hasPortalAccess list filter), so this is what tells the UI
    // whether a patient already has portal credentials.
    userId: apiPatient.userId ?? undefined,
    patientCode: apiPatient.patientCode ?? undefined,
    firstName: apiPatient.firstName,
    lastName: apiPatient.lastName,
    dateOfBirth: apiPatient.dateOfBirth,
    age: apiPatient.age,
    gender: apiPatient.gender,
    phone: apiPatient.phone,
    email: apiPatient.email || undefined,
    address: apiPatient.address,
    bloodType: apiPatient.bloodType || undefined,
    status: apiPatient.status,
    noShowCount: apiPatient.noShowCount ?? 0,
    isBlockedForBooking: apiPatient.isBlockedForBooking ?? false,
    erasedAt: apiPatient.erasedAt ?? null,
    emergencyContacts: apiPatient.emergencyContacts || [],
    householdMembers: apiPatient.householdMembers || [],
    allergies: apiPatient.allergies || [],
    conditions: apiPatient.conditions || [],
    rareChronicDiseases: apiPatient.rareChronicDiseases || [],
    medicalBackground: apiPatient.medicalBackground ?? null,
    insuranceProvider: apiPatient.insuranceProvider || undefined,
    insuranceId: apiPatient.insuranceId || undefined,
    insuranceType: apiPatient.insuranceType || undefined,
    nationalId: apiPatient.nationalId || undefined,
    socialSecurityNumber: apiPatient.socialSecurityNumber || undefined,
    vatNumber: apiPatient.vatNumber || undefined,
    preferredLanguage: apiPatient.preferredLanguage || 'pt',
    notificationPreferences: apiPatient.notificationPreferences ?? { email: true, sms: true },
    nationality: apiPatient.nationality || undefined,
    countryOfBirth: apiPatient.countryOfBirth || undefined,
    // Passed through as sent: null is "none", undefined is "this endpoint does not return it".
    lastVisit: apiPatient.lastVisit,
    nextAppointment: apiPatient.nextAppointment,
    createdAt: apiPatient.createdAt,
    updatedAt: apiPatient.updatedAt,
  };
};

export const mapCreatePatientToApi = (patient: Partial<Frontend.Patient>): API.CreatePatientDto => {
  return {
    firstName: patient.firstName!,
    lastName: patient.lastName!,
    dateOfBirth: patient.dateOfBirth!,
    gender: patient.gender!,
    phone: patient.phone!,
    email: patient.email,
    address: coerceAddress(patient.address),
    bloodType: patient.bloodType,
    allergies: patient.allergies,
    conditions: patient.conditions,
    rareChronicDiseases: patient.rareChronicDiseases,
    emergencyContacts: patient.emergencyContacts,
    insuranceProvider: patient.insuranceProvider,
    insuranceId: patient.insuranceId,
    insuranceType: patient.insuranceType,
    nationalId: patient.nationalId,
    socialSecurityNumber: patient.socialSecurityNumber,
    vatNumber: patient.vatNumber,
    householdMembers: patient.householdMembers,
    preferredLanguage: patient.preferredLanguage,
    nationality: patient.nationality,
    countryOfBirth: patient.countryOfBirth,
  };
};

// ============================================================================
// Doctor Mappers
// ============================================================================

export const mapDoctorFromApi = (apiDoctor: any): Frontend.Doctor => {
  // Backend column is `experience_years` exposed as `experienceYears`; the
  // frontend Doctor type calls it `experience`. Without this rename the
  // list/create/update responses leave doctor.experience undefined, which
  // the UI renders as "0 years" and the edit modal prefills as empty.
  const { experienceYears, ...rest } = apiDoctor ?? {};
  return {
    ...rest,
    ...(experienceYears !== undefined && experienceYears !== null && { experience: experienceYears }),
  } as Frontend.Doctor;
};

// ============================================================================
// Appointment Mappers
// ============================================================================

// The API returns `date` as a full ISO timestamp (the backend's `date` column
// is rehydrated into a JS Date and serialized as `YYYY-MM-DDT00:00:00.000Z`).
// The frontend treats `date` as a calendar day (`YYYY-MM-DD`), so we slice it
// here — otherwise calendar grids that compare `apt.date === 'YYYY-MM-DD'`
// would never match.
const toDateOnly = (value: unknown): string => {
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return '';
};

export const mapAppointmentFromApi = (apiAppointment: any): Frontend.Appointment => {
  return {
    ...apiAppointment,
    date: toDateOnly(apiAppointment?.date),
  } as Frontend.Appointment;
};

// ============================================================================
// Queue Mappers
// ============================================================================

const QUEUE_STATUS_MAP: Record<string, Frontend.QueueEntry['status']> = {
  CHECKED_IN: 'waiting',
  AWAITING_TRIAGE: 'waiting',
  IN_TRIAGE: 'in-progress',
  AWAITING_DOCTOR: 'waiting',
  AWAITING_NURSE: 'waiting',
  IN_CONSULTATION: 'in-progress',
  // Patient was called but didn't arrive — still "in flight" for the doctor's view,
  // re-callable from the doctor dashboard.
  NOT_PRESENT: 'waiting',
  CHECKED_OUT: 'completed',
  CANCELLED: 'completed',
  AUTO_EXPIRED: 'completed',
};

/**
 * Maps the backend 4-level priority enum (shared by `queue_entries.priority` and
 * `triage_records.priority_level`) to the 4 frontend labels.
 */
const TRIAGE_PRIORITY_MAP: Record<string, Frontend.QueueEntry['priority']> = {
  CRITICAL: 'emergency',
  URGENT: 'urgent',
  SEMI_URGENT: 'normal',
  NON_URGENT: 'low',
};

const toIsoString = (value: unknown): string => {
  if (!value) return '';
  if (value instanceof Date) return value.toISOString();
  return String(value);
};

const toIsoStringOrUndefined = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value.toISOString();
  return String(value);
};

const mapTriageVitals = (triage: any): Frontend.VitalSigns | undefined => {
  if (!triage) return undefined;
  const hasAnyVital =
    triage.temperature != null ||
    triage.heartRate != null ||
    triage.oxygenSaturation != null ||
    triage.respiratoryRate != null ||
    triage.weight != null ||
    triage.height != null ||
    triage.bloodPressureSystolic != null;

  if (!hasAnyVital) return undefined;

  return {
    bloodPressure:
      triage.bloodPressureSystolic != null && triage.bloodPressureDiastolic != null
        ? { systolic: triage.bloodPressureSystolic, diastolic: triage.bloodPressureDiastolic }
        : undefined,
    heartRate: triage.heartRate ?? undefined,
    temperature: triage.temperature ?? undefined,
    oxygenSaturation: triage.oxygenSaturation ?? undefined,
    respiratoryRate: triage.respiratoryRate ?? undefined,
    weight: triage.weight ?? undefined,
    height: triage.height ?? undefined,
  };
};

const mapWorkflowStage = (apiStatus: string, triage: any): Frontend.QueueEntry['workflowStage'] => {
  switch (apiStatus) {
    case 'CHECKED_IN':
    case 'AWAITING_TRIAGE':
      return 'pending-triage';
    case 'IN_TRIAGE':
      return 'in-triage';
    case 'AWAITING_DOCTOR':
      return triage?.isCompleted ? 'triaged' : 'waiting-doctor';
    case 'AWAITING_NURSE':
      // Nurse-led visits (vaccinations, treatments) skip triage. Surface as
      // "waiting-doctor" for now so the existing nurse queue UI keeps working;
      // a dedicated 'awaiting-nurse' workflow stage can be added in phase B
      // if the UX demands it.
      return 'waiting-doctor';
    case 'IN_CONSULTATION':
      return 'with-doctor';
    case 'NOT_PRESENT':
      // Doctor called but patient absent — still in the queue, re-callable.
      return 'waiting-doctor';
    case 'CHECKED_OUT':
    case 'CANCELLED':
    case 'AUTO_EXPIRED':
      return 'completed';
    default:
      return undefined;
  }
};

const mapTriageStatus = (apiStatus: string, triage: any): Frontend.QueueEntry['triageStatus'] => {
  if (triage?.isCompleted) return 'completed';
  if (triage) return 'in-progress';
  if (apiStatus === 'AWAITING_TRIAGE' || apiStatus === 'CHECKED_IN') return 'pending';
  return undefined;
};

export const mapQueueEntryFromApi = (apiEntry: any): Frontend.QueueEntry => {
  const triage = apiEntry.triageRecord;
  const apiStatus: string = apiEntry.status;

  // Triage assessment takes precedence over the check-in priority once it's set.
  const priority = TRIAGE_PRIORITY_MAP[triage?.priorityLevel ?? apiEntry.priority] ?? 'normal';

  return {
    id: apiEntry.id,
    queueNumber: apiEntry.queueNumber,
    patientId: apiEntry.patientId,
    patientCode: apiEntry.patientCode ?? undefined,
    patientName: apiEntry.patientName ?? '',
    status: QUEUE_STATUS_MAP[apiStatus] ?? 'waiting',
    apiStatus: apiStatus as Frontend.QueueEntry['apiStatus'],
    noShowCount: apiEntry.noShowCount ?? 0,
    pinnedAt: apiEntry.pinnedAt ? toIsoString(apiEntry.pinnedAt) : null,
    workflowStage: mapWorkflowStage(apiStatus, triage),
    priority,
    checkInTime: toIsoString(apiEntry.checkInTime),
    estimatedWaitTime: apiEntry.estimatedWaitTime ?? undefined,
    // Clinical specialty slug (e.g. `cardiology`). Surfaced so consumers like
    // the consultation page can load the specialty's dynamic form (CORA-170);
    // `department` keeps its prior fallback behaviour for display.
    specialty: apiEntry.specialty ?? undefined,
    department: apiEntry.department ?? apiEntry.specialty ?? '',
    triageStatus: mapTriageStatus(apiStatus, triage),
    triageStartTime: toIsoStringOrUndefined(triage?.triageStartTime ?? apiEntry.triageStartTime),
    triageCompletedTime: toIsoStringOrUndefined(triage?.triageEndTime ?? apiEntry.triageEndTime),
    triageNurseId: triage?.nurseId ?? undefined,
    triageNurseName: triage?.nurseName ?? undefined,
    triageNotes: triage?.assessmentNotes ?? undefined,
    doctorId: apiEntry.doctorId ?? undefined,
    doctorName: apiEntry.doctorName ?? undefined,
    nurseId: apiEntry.nurseId ?? undefined,
    nurseName: apiEntry.nurseName ?? undefined,
    appointmentId: apiEntry.appointmentId ?? undefined,
    consultationStartTime: toIsoStringOrUndefined(apiEntry.consultationStartTime),
    room: apiEntry.room ?? undefined,
    chiefComplaint: triage?.chiefComplaint ?? apiEntry.visitReason ?? undefined,
    vitals: mapTriageVitals(triage),
    createdAt: toIsoString(apiEntry.createdAt),
    updatedAt: toIsoString(apiEntry.updatedAt),
  };
};

// ============================================================================
// Vital Signs Mappers
// ============================================================================

export const mapVitalSignsFromApi = (apiVitals: any): Frontend.VitalSignsRecord => {
  return {
    ...apiVitals,
    recordedBy: apiVitals.recordedById || '',
    recordedByName: apiVitals.recorderName || '',
    bloodPressure: apiVitals.bloodPressureSystolic
      ? {
          systolic: apiVitals.bloodPressureSystolic,
          diastolic: apiVitals.bloodPressureDiastolic,
        }
      : undefined,
    painLevel: apiVitals.painScale,
    alertFlags: [],
    method: 'manual' as const,
    status: 'normal' as const,
  };
};

// ============================================================================
// Medical History Mappers
// ============================================================================

export const mapMedicalHistoryFromApi = (api: any): Frontend.MedicalHistoryRecord => {
  return {
    id: api.id,
    patientId: api.patientId,
    patientName: api.patientName,
    date: api.diagnosedDate || api.createdAt,
    type: api.entryType || 'consultation',
    title: api.title,
    description: api.description || '',
    doctorId: api.recordedById,
    doctorName: api.recorderName,
    diagnosis: api.icdCode ? `${api.icdCode} - ${api.title}` : undefined,
    notes: api.notes,
    location: api.facility,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
  };
};

// ============================================================================
// Vaccination Mappers
// ============================================================================

const doseNumberToLabel = (doseNumber?: number): Frontend.VaccineRecord['dose'] => {
  switch (doseNumber) {
    case 1:
      return '1ª';
    case 2:
      return '2ª';
    case 3:
      return '3ª';
    default:
      return 'única';
  }
};

export const mapVaccinationFromApi = (api: any): Frontend.VaccineRecord => {
  return {
    id: api.id,
    patientId: api.patientId,
    patientName: api.patientName,
    vaccineType: 'other',
    vaccineName: api.vaccineName,
    dose: doseNumberToLabel(api.doseNumber),
    lotNumber: api.batchNumber || '',
    lotDate: api.expirationDate || '',
    administeredDate: api.administeredDate,
    administeredBy: api.administeredById || '',
    administeredByName: api.administratorName || '',
    nextDoseDate: api.nextDoseDueDate,
    location: api.facility,
    notes: api.notes,
    sideEffects: api.adverseReactions,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
  };
};

// ============================================================================
// Prescription Mappers
// ============================================================================

export const mapPrescriptionFromApi = (api: any): Frontend.Prescription => {
  const startDate: string | undefined = api.prescribedDate || undefined;
  return {
    id: api.id,
    patientId: api.patientId,
    patientName: api.patientName,
    prescribedBy: api.prescribedById,
    prescribedByName: api.doctorName || '',
    prescribedDate: api.prescribedDate,
    status: api.status === 'dispensed' ? 'completed' : api.status || 'active',
    medications: (api.medications || []).map((med: any) => {
      const durationDays: number | null = typeof med.durationDays === 'number' ? med.durationDays : null;
      // Compute the end date when we know how long the course lasts. The
      // start date is the prescription's `prescribedDate` (YYYY-MM-DD).
      let endDate: string | undefined;
      if (durationDays !== null && startDate) {
        endDate = addCivilDays(formatCivilDate(startDate), durationDays);
      }
      return {
        id: med.id,
        name: med.medicationName,
        dosage: med.dosage,
        frequency: med.frequency,
        duration: durationDays !== null ? String(durationDays) : '',
        durationDays,
        instructions: med.instructions,
        startDate,
        endDate,
      };
    }),
    diagnosis: api.diagnosis,
    notes: api.notes,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
  };
};

// ============================================================================
// Referral Mappers
// ============================================================================

export const mapReferralFromApi = (api: any): Frontend.MedicalReferral => {
  return {
    id: api.id,
    patientId: api.patientId,
    patientName: api.patientName,
    referredBy: api.referringDoctorId,
    referredByName: api.referringDoctorName || '',
    referredFrom: api.referredToFacility || '',
    referredTo: api.referredToFacility || '',
    referredToSpecialty: api.referredToSpecialty,
    referredToDoctor: api.referredToDoctorName,
    referralDate: api.referralDate,
    appointmentDate: api.appointmentDate,
    reason: api.reasonForReferral,
    urgency: api.priority || 'routine',
    status: api.status || 'pending',
    diagnosis: api.diagnosis,
    clinicalSummary: api.clinicalSummary,
    documents: api.attachedDocuments,
    notes: api.notes,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
  };
};

// ============================================================================
// Invoice Mappers
// ============================================================================

export const mapInvoiceFromApi = (apiInvoice: any): Frontend.Invoice => {
  return {
    id: apiInvoice.id,
    invoiceNumber: apiInvoice.invoiceNumber,
    patientId: apiInvoice.patientId,
    patientName: apiInvoice.patientName,
    patientCode: apiInvoice.patientCode ?? null,
    date: apiInvoice.issueDate,
    dueDate: apiInvoice.dueDate,
    status: (apiInvoice.status || '').toLowerCase().replace('_', '-'),
    items: (apiInvoice.items || []).map((item: any) => ({
      id: item.id,
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      total: item.total,
    })),
    subtotal: apiInvoice.subtotal,
    tax: apiInvoice.taxAmount,
    total: apiInvoice.total,
    amountPaid: apiInvoice.amountPaid,
    amountDue: apiInvoice.amountDue,
    paidDate: apiInvoice.paidDate,
    notes: apiInvoice.notes,
    createdAt: apiInvoice.createdAt,
    updatedAt: apiInvoice.updatedAt,
  };
};

// ============================================================================
// Payment Mappers
// ============================================================================

export const mapPaymentFromApi = (api: any): Frontend.Payment => {
  return {
    id: api.id,
    invoiceId: api.invoiceId,
    amount: api.amount,
    method: (api.method || '').toLowerCase() as Frontend.Payment['method'],
    reference: api.reference || api.transactionId,
    date: api.processedAt || api.createdAt,
    createdBy: api.processedBy,
  };
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Maps a paginated API response to frontend format
 */
export const mapPaginatedResponse = <T, U>(
  apiResponse: { items: T[]; total: number; page: number; pageSize: number },
  mapper: (item: T) => U,
): { items: U[]; total: number; page: number; pageSize: number } => {
  return {
    items: apiResponse.items.map(mapper),
    total: apiResponse.total,
    page: apiResponse.page,
    pageSize: apiResponse.pageSize,
  };
};

/**
 * Maps an API error to a user-friendly message in the active locale; see translateApiError. `fallbackKey` is the
 * caller's own message for a failure no domain code explains.
 */
export const mapApiError = (error: unknown, fallbackKey?: string): string => translateApiError(error, fallbackKey);
